// src/contexts/OrderContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import axiosInstance from "../utils/axiosInstance";
import { message } from "antd";

const OrderContext = createContext();

export const OrderProvider = ({ children }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [deliveryStatuses, setDeliveryStatuses] = useState([]);
  const [manufacturingStatuses, setManufacturingStatuses] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const userRole = localStorage.getItem("userRole") || "admin";
  const isSales = userRole === "sales";
  const isOrderCanceller = userRole === "order_canceller";

  // Production status labels (unchanged)
  const productionStatusLabels = {
    1: "Pending",
    2: "In Progress",
    3: "Completed",
    4: "Cancelled",
  };

  // Helper function to fetch documents for an order
  const fetchOrderDocuments = async (orderId) => {
    try {
      const token = localStorage.getItem("token");
      const docRes = await axiosInstance.get(
        `/order/order-documents/${orderId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (docRes.data.success) {
        return docRes.data.documents;
      }
    } catch {
      // If 404 or any error, return empty array (no documents)
      return [];
    }
    return [];
  };

  // ───────────────────────────────────────────────────────────
  // Helpers to normalize category names for display (singular)
  // ───────────────────────────────────────────────────────────
  const singularizeCategory = (raw) => {
    const s = String(raw || "").trim();
    const low = s.toLowerCase();
    if (low === "beds")        return "Bed";
    if (low === "mattresses")  return "Mattress";
    if (low === "sofas")       return "Sofa";
    if (low === "headboards")  return "Headboard";
    if (low === "footstools")  return "Footstool";
    // keep custom names as-is (e.g. "Kids Bed", "Clearance")
    return s || "Other";
  };

  const choosePrimaryCategory = (names) => {
    const lows = names.map((n) => String(n || "").toLowerCase());
    // Prefer Bed over Mattress for ordering/grouping
    if (lows.includes("beds"))       return "Bed";
    if (lows.includes("mattresses")) return "Mattress";
    // fallback to first known or "Other"
    return singularizeCategory(names[0] || "Other");
  };

  // ───────────────────────────────────────────────────────────
  // Fetch all orders with their documents
  // ───────────────────────────────────────────────────────────
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axiosInstance.get("/order/orders", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (Array.isArray(data.orders)) {
        // Fetch documents for orders only when there's an indication they exist
        const ordersWithDocs = await Promise.all(
          data.orders.map(async (order) => {
            // ✅ Only hit /order/order-documents/:id if the order suggests docs exist
            const shouldFetchDocs = Boolean(
              order?.hasDocuments === true ||
              (typeof order?.document_count === "number" && order.document_count > 0) ||
              (Array.isArray(order?.documents) && order.documents.length > 0)
            );

            const documents = shouldFetchDocs
              ? await fetchOrderDocuments(order.id)
              : [];

            // ----- CATEGORY FIX (derive from product.Categories if server didn't add) -----
            const rawCats =
              (order.product_categories && Array.isArray(order.product_categories))
                ? order.product_categories
                : (order.product?.Categories || []).map((c) => c.category_name);

            const productCategoryDisplay =
              order.productCategoryDisplay ||
              choosePrimaryCategory(rawCats);

            return {
              // keep all fields the API provides
              ...order,

              // normalized aliases
              order_date: order.orderDate,
              referenceNumber: order.referenceNumber || "",
              documents,

              // make sure these exist in state for the UI:
              deliveryStatus: order.deliveryStatus,
              deliveredAt: order.deliveredAt || order.delivered_at || null,
              scheduleDate: order.scheduleDate || order.schedule_date || "",

              productionStatus: order.productionStatus,
              priority: order.priority,
              trackingId: order.trackingId || "N/A",
              product_image: order.product_image || "",
              product_name: order.product_name || order.productName || "",
              total_price: order.total_price || "",
              factory_price:
                order.factoryPrice || order.factory_price || "",
              full_name: order.full_name || "",
              cancellationReason:
                order.cancellationReason || order.cancelReason || "",

              // 👉 expose categories cleanly for the UI
              product_categories: rawCats,
              productCategoryDisplay,

              // ADD STOCK INFORMATION
              product: order.product
                ? {
                    ...order.product,
                    stock: order.product.stock || 0,
                  }
                : null,
            };
          })
        );

        setOrders(ordersWithDocs);
        setPagination((prev) => ({
          ...prev,
          total: ordersWithDocs.length,
        }));
      } else {
        setOrders([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      }
    } catch (err) {
      setOrders([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
      if (err.response?.status !== 404) {
        message.error(
          err.response?.data?.message ||
            err.response?.data?.error ||
            "Failed to load orders."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // ───────────────────────────────────────────────────────────
  // Fetch delivery statuses
  // ───────────────────────────────────────────────────────────
  const fetchDeliveryStatuses = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data, status } = await axiosInstance.get(
        "/order/delivery/status",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (status === 200 && Array.isArray(data?.deliveryStatuses)) {
        setDeliveryStatuses(data.deliveryStatuses);
      }
    } catch (err) {
      message.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to load delivery statuses."
      );
    }
  };

  // ───────────────────────────────────────────────────────────
  // Fetch manufacturing statuses
  // ───────────────────────────────────────────────────────────
  const fetchManufacturingStatuses = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data, status } = await axiosInstance.get(
        "/order/manufacturing/status",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (status === 200 && Array.isArray(data?.manufacturingStatuses)) {
        setManufacturingStatuses(data.manufacturingStatuses);
      }
    } catch (err) {
      message.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to load manufacturing statuses."
      );
    }
  };

  // ───────────────────────────────────────────────────────────
  // Generic PUT helper (unchanged)
  // ───────────────────────────────────────────────────────────
  const updateOrderField = async (url, payload, successMessage = "Updated") => {
    try {
      const token = localStorage.getItem("token");
      const { data, status } = await axiosInstance.put(url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const ok =
        (status >= 200 && status < 300 && data?.success !== false) ||
        data?.success === true;

      if (ok) {
        message.success(data?.message || successMessage);
        fetchOrders();
        return true;
      }

      message.error(data?.message || "Update failed.");
      return false;
    } catch (err) {
      message.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Update failed."
      );
      return false;
    }
  };

  // ───────────────────────────────────────────────────────────
  // Update order status  (supports 'completed')
  // ───────────────────────────────────────────────────────────
  const updateOrderStatus = (orderId, status) => {
    return updateOrderField(
      `/order/status/${orderId}`,
      { status },
      "Status updated"
    );
  };

  // Update order priority
  const updateOrderPriority = (orderId, priority) => {
    return updateOrderField(
      `/order/priority/${orderId}`,
      { priority },
      "Priority updated"
    );
  };

  // Update delivery status
  const updateDeliveryStatus = (orderId, status) => {
    return updateOrderField(
      `/order/delivery/status`,
      { id: orderId, status },
      "Delivery status updated"
    );
  };

  // Update manufacturing / production status
  const updateManufacturingStatus = (orderId, status) => {
    return updateOrderField(
      `/order/manufacturing/status`,
      { id: orderId, manufacturingStatus: status },
      "Production status updated"
    );
  };

  // Cancel order with reason
  const cancelOrderWithReason = async (orderId, reason) => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axiosInstance.put(
        `/order/cancel-with-reason/${orderId}`,
        {
          reason,
          status: "cancelled",
          cancelReason: reason,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data?.success) {
        message.success(data?.message || "Order cancelled successfully");
        fetchOrders();
        return true;
      } else {
        message.error(data?.message || "Failed to cancel order");
        return false;
      }
    } catch (err) {
      message.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to cancel order"
      );
      return false;
    }
  };

  // Delete order
  const deleteOrder = async (orderId) => {
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.delete(`/order/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success(`Order #${orderId} deleted.`);
      fetchOrders();
      return true;
    } catch (err) {
      message.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to delete order."
      );
      return false;
    }
  };

  // Schedule orders
  const scheduleOrders = async (orderIds, scheduleDate) => {
    try {
      const token = localStorage.getItem("token");
      await Promise.all(
        orderIds.map((id) =>
          axiosInstance.put(
            `/order/schedule-date/${id}`,
            {
              scheduleDate: scheduleDate.toISOString(),
              deliveryStatus: "scheduled",
            },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      message.success("Orders scheduled successfully!");
      setSelectedRowKeys([]);
      setSelectedRows([]);
      fetchOrders();
      return true;
    } catch (err) {
      message.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Scheduling failed."
      );
      return false;
    }
  };

  // Initial data loading
  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        fetchOrders(),
        fetchDeliveryStatuses(),
        fetchManufacturingStatuses(),
      ]);
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <OrderContext.Provider
      value={{
        orders,
        loading,
        pagination,
        setPagination,
        deliveryStatuses,
        manufacturingStatuses,
        productionStatusLabels,
        selectedRows,
        selectedRowKeys,
        setSelectedRows,
        setSelectedRowKeys,
        userRole,
        isSales,
        isOrderCanceller,
        fetchOrders,
        updateOrderStatus,
        updateOrderPriority,
        updateDeliveryStatus,
        updateManufacturingStatus,
        cancelOrderWithReason,
        deleteOrder,
        scheduleOrders,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

export const useOrderContext = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error("useOrderContext must be used within an OrderProvider");
  }
  return context;
};
